import React, {FC} from "react";
import {useHistory, useParams, useRouteMatch} from "react-router-dom";
import {Button, Container, Grid, LinearProgress, Typography} from "@material-ui/core";
import {useOrder} from "../../orders/redux/orders.hook";
import {formatDate} from "../../../date.utils";
import {LabeledTextDisplay} from "../../../sharedComponents/LabeledTextDisplay";
import {getFullName} from "../../../utils";
import {makeStyles} from "@material-ui/core/styles";
import {InfoOutlined} from "@material-ui/icons";
import {ServiceChips} from "../../services/components/ServiceChip";
import {isAfter, isBefore, isToday, subDays} from "date-fns";
import {OrderStatusChip} from "../../orders/sharedComponents/OrderStatusChip";
import {CancelOrderDialog} from "../../orders/sharedComponents/CancelOrderDialog";
import {FinalizeWorkerOrderDialog} from "./FinalizeWorkerOrderDialog";
import {NextAppointmentDialog} from "./NextAppointmentDialog";

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(2),
        position: "relative"
    },
    headline: {
        marginBottom: theme.spacing(2)
    },
    spacingBottom: {
        marginBottom: theme.spacing(1)
    },
    cancelOrderButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText
    },
    finalizedOrderButton: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.error.contrastText
    }
}));

export const WorkerOrderScreen: FC = () => {
    const classes = useStyles();
    const {orderId} = useParams<{ orderId: string }>();
    const {order} = useOrder(orderId);
    const {url} = useRouteMatch();
    const history = useHistory();

    const isDayOfOrder = order && isToday(order.estimatedFrom);
    const isEqualOrAfterDate = isDayOfOrder || (order && isAfter(new Date(), order.estimatedTo));
    const isMoreThanTwoDaysBefore = order && isBefore(new Date(), subDays(order.estimatedFrom, 2));

    const [finalizeDialogOpen, setFinalizeDialogOpen] = React.useState(false);
    const toggleFinalizeDialog = () => setFinalizeDialogOpen(p => !p);

    const [newAppointmentDialogOpen, setNewAppointmentDialogOpen] = React.useState(false);
    const toggleNewAppointmentDialog = () => setNewAppointmentDialogOpen(p => !p);

    const navigateToCancelOrder = () => history.replace(`${url}?mode=edit#cancel-order`);

    return (
        <Container className={classes.container}>
            <Typography color="primary" variant="h5" className={classes.headline}>
                Auftrag
            </Typography>
            {order && <OrderStatusChip order={order} chipProps={{style: {position: "absolute", right: 0, top: 16}}}/>}
            {
                order
                    ? (
                        <div>
                            <CancelOrderDialog order={order} type="worker"/>
                            <NextAppointmentDialog
                                open={newAppointmentDialogOpen}
                                toggleDialog={toggleNewAppointmentDialog}
                                oldOrder={order}
                            />
                            <FinalizeWorkerOrderDialog
                                dialogOpen={finalizeDialogOpen}
                                toggleDialog={toggleFinalizeDialog}
                                order={order}
                            />
                            <Typography
                                variant="h6"
                                color="secondary"
                            >
                                {formatDate(order.estimatedFrom, "EEEE, dd.MM.yyyy")}
                            </Typography>
                            <Typography color="secondary" className={classes.spacingBottom}>
                                {formatDate(order.estimatedFrom, "HH:mm")} - {formatDate(order.estimatedTo, "HH:mm")}
                            </Typography>
                            {order.from && order.to &&
                            <LabeledTextDisplay
                                label={"Ist Zeit"}
                                value={`${formatDate(order.from, "HH:mm")} - ${formatDate(order.to, "HH:mm")}`}
                                className={classes.spacingBottom}
                            />}
                            <LabeledTextDisplay
                                label={"Kunde"}
                                value={getFullName(order.serviceAddress.name)}
                                type={"name"}
                                className={classes.spacingBottom}
                            />
                            <LabeledTextDisplay
                                label={"Ort"}
                                value={order.serviceAddress.address.formattedAddress}
                                type={"address"}
                                className={classes.spacingBottom}
                            />
                            <LabeledTextDisplay
                                label={"Telefon"}
                                value={order.serviceAddress.phone}
                                className={classes.spacingBottom}
                                type={"phone"}
                            />
                            <div className={classes.spacingBottom}>
                                <Typography variant="caption" color="textSecondary">Dienstleistungen</Typography>
                                <ServiceChips serviceIds={order.servicesIds}/>
                            </div>
                            {order.extraInfo &&
                            <div style={{display: "flex", marginTop: 24, marginBottom: 24}}>
                                <InfoOutlined color="primary"/>
                                <Typography variant="body2" style={{marginLeft: 8}} color="textSecondary">
                                    {order.extraInfo}
                                </Typography>
                            </div>}
                            <Grid container spacing={2} style={{marginTop: 16}}>
                                {isMoreThanTwoDaysBefore && !order.cancellation && !order.from && !order.to &&
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={navigateToCancelOrder}
                                        className={classes.cancelOrderButton}
                                    >
                                        Absagen
                                    </Button>
                                </Grid>}
                                {isEqualOrAfterDate &&
                                !order.from && !order.to &&
                                <Grid item xs={12}>
                                    <Button
                                        onClick={toggleFinalizeDialog}
                                        variant="contained"
                                        className={classes.finalizedOrderButton}
                                    >
                                        Arbeitszeit buchen
                                    </Button>
                                </Grid>}
                                {isDayOfOrder &&
                                <Grid item xs={12}>
                                    <Button variant="contained" onClick={toggleNewAppointmentDialog}>
                                        Folgetermin vereinbaren
                                    </Button>
                                </Grid>}
                            </Grid>
                        </div>
                    )
                    : <LinearProgress/>

            }
        </Container>
    )
};
